import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Loader } from "lucide-react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify } from "../../../helpers";
import { getAllPracticeData, getPracticePermissionData, updatePracticePermissions } from "../../../services/users";
import Grid from "../../Grid";
import Skeleton from "react-loading-skeleton";

export default function PermissionSettings({selectedPracticeChange, fetchChangePracticeData}) {

  let portal_account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    portal_account_id = selectedPracticeChange?.account_id;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [reloadData, setReloadData] = useState(true);
  const [accountsData, setAccountsData] = useState([]);
  const [featureConfigsData, setFeatureConfigsData] = useState([]);
  const [checkAll, setCheckAll] = useState(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  useEffect(() => {
    if(reloadData) {
      fetchAllAccountsData();
      fetchFeatureConfigData();
    }
  }, [reloadData]);

  const fetchAllAccountsData = async () => {
    setEnableLoader(true);
    try {
      const response = await getAllPracticeData();
      if (response) {
        const account_data = response?.data?.map((items) => ({
          value: items?.account_id,
          label: items?.account_name,
        }));
        setAccountsData(account_data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchFeatureConfigData = async (accountId) => {
    setEnableLoader(true);
    try {
      const response = await getPracticePermissionData(accountId);
      if (response?.data) {
        const sortedResponse = response?.data.sort((a, b) => a.name.localeCompare(b.name));
        const fetchedData = sortedResponse.map((item) => ({
          ...item,
          ui_value: !!(item?.value && item?.system_value && item?.is_enabled),
          value: item?.value,
        }));
        
        setFeatureConfigsData(fetchedData);
      } else {
        setFeatureConfigsData([]);
      }
      setReloadData(false);
    } catch (error) {
      setFeatureConfigsData([]);
      console.error("Error fetching data:", error);
    } finally {
      setEnableLoader(false);
    }
  };

  const SplitGridFeaturs = (sortedResponse, parts) => {
    const chunkSize = Math.ceil(sortedResponse?.length / parts);
    return Array.from({ length: parts }, (_, i) =>
      sortedResponse?.slice(i * chunkSize, i * chunkSize + chunkSize)
    );
  };

  const [firstColumn, secondColumn, thirdColumn, fourthColumn] = SplitGridFeaturs(featureConfigsData, 4);

  const handleSelectChange = async (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedAccountIds([]);
      return;
    }
  
    const selectAllOption = selectedOptions.find(
      (option) => option.value === "select_all"
    );
    const unselectAllOption = selectedOptions.find(
      (option) => option.value === "unselect_all"
    );
  
    // Select all accounts
    if (selectAllOption) {
      const allAccountIds = accountsData.map((account) => account.value);
      setSelectedAccountIds(allAccountIds);
      return;
    }

    // Unselect all accounts
    if (unselectAllOption) {
      setSelectedAccountIds([]);
      return;
    }
  
    // Handle normal account selection
    const selectedIds = selectedOptions.map((option) => option.value);
    setSelectedAccountIds(selectedIds);
  
    if (selectedIds.length === 1) {
      await fetchFeatureConfigData(selectedIds[0]);
    }
  };  

  const handleCheckAll = (isChecked) => {
    setCheckAll(isChecked);
    setFeatureConfigsData((prevData) =>
      prevData.map((item) => ({
        ...item,
        ui_value: isChecked
      }))
    );
  };

  const handleSwitchChange = (id, checked) => {  
    setFeatureConfigsData((prevData) =>
      prevData.map((item) => item.id === id ? { ...item, ui_value: checked } : item)
    );
  };

  const handleFeatureConfigUpdate = async (e) => {
    e.preventDefault();

    if(can_visit("administrator") && can_visit("administrator_permissions")) {
      try {
        setEnableLoader(true);

        const updatedPermissionData = featureConfigsData.map(({ id, ui_value }) => ({
          id,
          value: !!ui_value,
        }));
        const account_id_for_update = selectedAccountIds.length === 1
          ? selectedAccountIds[0]
          : portal_account_id;
        const updatedData = {
          account_id: account_id_for_update,
          account_ids: selectedAccountIds,
          permissions: updatedPermissionData,
        };

        const response = await updatePracticePermissions(updatedData);

        if (response?.detail) {
          notify('error', response?.detail);
        } else { 
          if(response?.data?.status === 'success'){
            notify("success", response?.data?.message);
          } else {
            notify("error", response?.data?.message);
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally{
        setEnableLoader(false);
      }
    }
  };

  const FeatureList = ({ items }) => (
    items?.map((item, index) => (
      item?.name?.toLowerCase().includes('_role_') ? null : (
        <div key={item?.id ?? index} className="docpro_checkbox">
          <input
            type="checkbox"
            name="feature_toggle"
            checked={!!item?.ui_value}
            onChange={(e) => handleSwitchChange(item?.id, e.target.checked)}
          />
          <label style={{ marginLeft: "6px" }} className={`${item?.system_value !== true && 'docpro_red_color'}`}>{item?.name}</label>
        </div>
      )
    ))
  );

  useDocumentTitle("Practice Permissions");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          Practice Permissions
        </h2>
      </div>
      <Spacing lg="5" sm="10" />

      <div className="docpro_card">
        {reloadData ? (
          <>
            <Skeleton width="10rem" borderRadius="8px"></Skeleton>
            <Skeleton width="100%" height="3rem" borderRadius="8px"></Skeleton>
            <Spacing lg="15" sm="20" />
            <Skeleton width="10rem" borderRadius="8px"></Skeleton>
            <Spacing lg="15" sm="20" />
            <Grid variant="docpro_grid_gap_10 docpro_grid_col_4">
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["6rem","7rem","6rem","5rem","6rem","8rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["7.5rem","7rem","6rem","4rem","6rem","8rem","9rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["5rem","7rem","8rem","5rem","6rem","4rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
              <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                {["8rem","7rem","9rem","5rem","5rem","8rem","7rem"].map((w, i) => (
                  <Skeleton key={i} width={w} borderRadius="8px" />
                ))}
              </Grid>
            </Grid>
          </>
        ) : (
          <form onSubmit={handleFeatureConfigUpdate} autoComplete="off" className="relative">
            <label className="docpro_fw_500 docpro_primary_color">
              Select Practice <span className="docpro_red_color">*</span>
            </label>
            <Select
              required
              name="account_id"
              options={[
                ...(selectedAccountIds.length === accountsData.length
                  ? [{ value: "unselect_all", label: "Unselect All" }]
                  : [{ value: "select_all", label: "Select All" }]),
                ...accountsData,
              ]}
              placeholder="Search..."
              isSearchable={true}
              isMulti
              value={accountsData.filter((option) =>
                selectedAccountIds.includes(option.value)
              )}
              onChange={(selectedOptions) => handleSelectChange(selectedOptions)}
              className="react-select-container w-full"
              classNamePrefix="react-select"
              filterOption={(option, rawInput) => {
                const words = rawInput.toLowerCase().split(" ");
                return words.every((word) =>
                  option.label.toLowerCase().includes(word)
                );
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  borderColor: 'var(--border)',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 1050,
                  marginTop: '-2px',
                  borderRadius: '0 0 3px 3px',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 1050,
                }),
              }}
            />
            <Spacing lg="15" sm="25" />

            <div className="flex items-center justify-between docpro_grid_gap_20">
              <h2 className="docpro_fs_16 docpro_mb_0">Set Feature Permissions</h2>

              <div className="text-right" style={{ minWidth: '125px' }}>
                <div className="docpro_checkbox">
                  <input
                    type="checkbox"
                    name="check_all_yes"
                    checked={checkAll === true}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                  />
                  <label style={{ marginLeft: "8px" }}>Check All</label>
                </div>
              </div>
            </div>
            <Spacing lg="15" sm="25" />

            {featureConfigsData.length > 0 ? (
              <>
                <Grid variant="docpro_grid_gap_10 docpro_grid_col_4">
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={firstColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={secondColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={thirdColumn} />
                  </Grid>
                  <Grid variant="docpro_grid_gap_10 docpro_grid_col_1">
                    <FeatureList items={fourthColumn} />
                  </Grid>
                </Grid>
              </>
            ) : (
              <div style={{ height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                No data available
              </div>
            )}

            <Spacing lg="32" sm="32" />
            <div className="docpro_modal_btns">
              <button
                className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: `${enableLoader ? "none" : "initial"}` }}
                type="submit"
                disabled={enableLoader}
              >
                Save
                {enableLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </div>

            {enableLoader && (
              <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
                <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)' }}>
                  <span className="docpro_loading" style={{ position: 'inherit' }}>
                    <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                  </span>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </>
  );
}
